import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';
import ThemeToggle from '../ThemeToggle';
import { t } from '../../utils/translations';
import ChatTab from './ChatTab';
import ConversationsTab from './ConversationsTab';
import SubjectsTab from './SubjectsTab';
import SubjectModal from './SubjectModal';
import QuizTab from './QuizTab';

// Conversation types - must match backend ConversationType enum
const CONVERSATION_TYPES = {
  GENERAL: 'general',
  SUBJECT_LEARNING: 'subject_learning',
  SECTION_LEARNING: 'section_learning',
  QUIZ: 'quiz',
  TODAY_SECTION: 'today_section'
};

const Rulee = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'conversations', or 'subjects'
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectModalOpen, setSubjectModalOpen] = useState(false);
  const [showTodaySectionForm, setShowTodaySectionForm] = useState(false);
  const [todayTopic, setTodayTopic] = useState('');
  const [todaySection, setTodaySection] = useState('');
  const [quizSource, setQuizSource] = useState('auto');
  const [quizQuestionCount, setQuizQuestionCount] = useState(6);
  const [selectedQuizSectionIds, setSelectedQuizSectionIds] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Try to load language from localStorage first, then from API
  const [userLanguage, setUserLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('userLanguage');
    return savedLanguage || 'en';
  });
  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [todaySections, setTodaySections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [showSectionSelection, setShowSectionSelection] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [completedQuizzes, setCompletedQuizzes] = useState([]);
  const [loadingQuizHistory, setLoadingQuizHistory] = useState(false);
  const [activeQuizzes, setActiveQuizzes] = useState([]);
  const [loadingActiveQuizzes, setLoadingActiveQuizzes] = useState(false);
  const [openingActiveQuiz, setOpeningActiveQuiz] = useState(false);
  const [loadingQuizReview, setLoadingQuizReview] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [quizOverlayOpen, setQuizOverlayOpen] = useState(false);
  const [quizReturnPanel, setQuizReturnPanel] = useState(null);
  const { logout, isParent } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loadMessages = async (conversationId) => {
    try {
      const response = await api.get(`/conversations/${conversationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
      // Error handling is done by the global interceptor
    }
  };

  const showAppAlert = useCallback((message) => {
    setAlertMessage(String(message || t('errorSendingMessage', userLanguage)));
  }, [userLanguage]);

  const loadChildConversationMessages = useCallback(async (childId, conversationId) => {
    try {
      const response = await api.get(`/parent/children/${childId}/conversations/${conversationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading child conversation messages:', error);
      showAppAlert(handleApiError(error, t('loadingConversations', userLanguage)));
    }
  }, [showAppAlert, userLanguage]);

  useEffect(() => {
    loadUserInfo();
    loadConversations();
    loadQuizHistory();
    loadActiveQuizzes();
  }, []);

  useEffect(() => {
    if (activeTab === 'conversations') {
      loadConversations();
    } else if (activeTab === 'chat' && currentConversation && messages.length === 0) {
      // If switching to chat tab and there's an active conversation but no messages, load them
      loadMessages(currentConversation.id);
    }
  }, [activeTab, currentConversation, messages.length]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeTab]);

  const loadUserInfo = async () => {
    try {
      setLoadingUserInfo(true);
      const response = await api.get('/me');
      if (response.data) {
        // Set user ID
        if (response.data.id) {
          setUserId(response.data.id);
        }
        // Set language
        if (response.data.language) {
          const language = response.data.language;
          setUserLanguage(language);
          // Save to localStorage for faster loading next time
          localStorage.setItem('userLanguage', language);
        } else {
          const defaultLang = 'en';
          setUserLanguage(defaultLang);
          localStorage.setItem('userLanguage', defaultLang);
        }
        // Set username
        if (response.data.username) {
          setUserName(response.data.username);
        }
      } else {
        const defaultLang = 'en';
        setUserLanguage(defaultLang);
        localStorage.setItem('userLanguage', defaultLang);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      // Default to English if error, but keep what's in localStorage
      const savedLanguage = localStorage.getItem('userLanguage') || 'en';
      setUserLanguage(savedLanguage);
    } finally {
      setLoadingUserInfo(false);
    }
  };

  const loadAssignedClasses = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoadingClasses(true);
      const response = await api.get(`/subjects/my-subjects?language=${userLanguage}`);
      const assignedSubjects = response.data || [];

      // Fallback: if user has no explicit assignments yet, show available subjects
      // so the School Classes tab does not appear empty.
      if (assignedSubjects.length === 0) {
        const fallbackResponse = await api.get(`/subjects?language=${userLanguage}&include_inactive=false`);
        setAssignedClasses(fallbackResponse.data || []);
      } else {
        setAssignedClasses(assignedSubjects);
      }
    } catch (error) {
      console.error('Error loading assigned classes:', error);
      // If assigned-subject lookup fails, try loading available subjects as fallback.
      try {
        const fallbackResponse = await api.get(`/subjects?language=${userLanguage}&include_inactive=false`);
        setAssignedClasses(fallbackResponse.data || []);
      } catch (fallbackError) {
        console.error('Error loading fallback classes:', fallbackError);
        setAssignedClasses([]);
      }
    } finally {
      setLoadingClasses(false);
    }
  }, [userId, userLanguage]);

  useEffect(() => {
    if (userId) {
      loadAssignedClasses();
    }
  }, [userId, loadAssignedClasses]);

  // Check for conversation ID in URL params
  useEffect(() => {
    const conversationIdParam = searchParams.get('conversationId');
    const childIdParam = searchParams.get('childId');
    
    if (conversationIdParam) {
      const convId = parseInt(conversationIdParam);
      if (!isNaN(convId)) {
        if (childIdParam) {
          // This is a child conversation - load it differently
          const childId = parseInt(childIdParam);
          const titleParam = searchParams.get('title');
          const conversationTitle = titleParam ? decodeURIComponent(titleParam) : t('newConversation', userLanguage);
          if (!isNaN(childId)) {
            loadChildConversationMessages(childId, convId);
            setCurrentConversation({ id: convId, title: conversationTitle });
          }
        } else {
          // Regular conversation - find it in loaded conversations
          const conversation = conversations.find(c => c.id === convId);
          if (conversation) {
            setCurrentConversation(conversation);
          } else {
            // If not found, try to load it directly
            loadMessages(convId);
            setCurrentConversation({ id: convId, title: t('loading', userLanguage) });
          }
        }
        // Remove the param from URL
        navigate('/rulee', { replace: true });
      }
    }
  }, [searchParams, conversations, navigate, loadChildConversationMessages, userLanguage]);

  useEffect(() => {
    // Only load messages if conversation was set from sidebar, not from URL params
    // (URL params are handled in the other useEffect)
    if (currentConversation && !searchParams.get('conversationId')) {
      loadMessages(currentConversation.id);
    } else if (!currentConversation) {
      setMessages([]);
    }
  }, [currentConversation, searchParams]);

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const response = await api.get('/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error loading conversations:', error);
      // Error handling is done by the global interceptor
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadQuizHistory = async () => {
    try {
      setLoadingQuizHistory(true);
      const response = await api.get('/learning/quiz/history');
      setCompletedQuizzes(response.data || []);
    } catch (error) {
      console.error('Error loading quiz history:', error);
      setCompletedQuizzes([]);
    } finally {
      setLoadingQuizHistory(false);
    }
  };

  const loadActiveQuizzes = async () => {
    try {
      setLoadingActiveQuizzes(true);
      const response = await api.get('/learning/quiz/active');
      setActiveQuizzes(response.data || []);
    } catch (error) {
      console.error('Error loading active quizzes:', error);
      setActiveQuizzes([]);
    } finally {
      setLoadingActiveQuizzes(false);
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
  };

  const handleChatTabClick = () => {
    startNewConversation();
    setActiveTab('chat');
  };

  const selectConversation = (conversation) => {
    setCurrentConversation(conversation);
    loadMessages(conversation.id);
    setActiveTab('chat'); // Switch to chat tab to see messages
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    // Add user message to UI immediately
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const response = await api.post('/chat', {
        message: userMessage,
        conversation_id: currentConversation?.id,
        conversation_type: CONVERSATION_TYPES.GENERAL  // Always send conversation_type
      });

      const { response: aiResponse, conversation_id } = response.data;

      // Add AI response to UI
      const newAiMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, newAiMessage]);

      // Update current conversation if it's a new one
      if (!currentConversation) {
        // Reload conversations to get the full conversation data including type
        const updatedConversations = await api.get('/conversations');
        const newConv = updatedConversations.data.find(c => c.id === conversation_id);
        if (newConv) {
          setCurrentConversation(newConv);
          setConversations(updatedConversations.data);
        } else {
          // Fallback: create basic conversation object
          const newConversation = {
            id: conversation_id,
            title: userMessage.length > 50 ? userMessage.substring(0, 50) + '...' : userMessage,
            created_at: new Date().toISOString(),
            conversation_type: CONVERSATION_TYPES.GENERAL
          };
          setCurrentConversation(newConversation);
          setConversations(prev => [newConversation, ...prev]);
        }
      } else {
        // Reload conversations to get updated titles and types
        loadConversations();
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
      const errorMessage = handleApiError(error, t('errorSendingMessage', userLanguage));
      showAppAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
    setSubjectModalOpen(true);
  };

  const closeSubjectModal = () => {
    setSubjectModalOpen(false);
    setSelectedSubject(null);
    setShowTodaySectionForm(false);
    setShowSectionSelection(false);
    setTodayTopic('');
    setTodaySection('');
    setTodaySections([]);
    setQuizSource('auto');
    setQuizQuestionCount(6);
    setSelectedQuizSectionIds([]);
  };

  const handleAddTodaysSection = () => {
    if (!selectedSubject || loading) return;
    setShowTodaySectionForm(true);
  };

  const handleSubmitTodaysSection = async (e) => {
    e.preventDefault();
    if (!selectedSubject || loading) return;
    
    if (!todayTopic.trim() || !todaySection.trim()) {
      showAppAlert(t('pleaseFillFields', userLanguage));
      return;
    }
    
    setLoading(true);
    try {
      // Create today's section via API
      await api.post('/learning/today-sections', {
        subject_id: selectedSubject.id,
        topic: todayTopic.trim(),
        section_content: todaySection.trim()
      });
      
      closeSubjectModal();
      
      // Also send message to chat for AI interaction
      const message = `${t('addTodaysSectionMessage', userLanguage)} ${selectedSubject.name} (${selectedSubject.key}).\n\n${t('topic', userLanguage)}: ${todayTopic}\n${t('section', userLanguage)}: ${todaySection}\n\n${t('helpUnderstandMessage', userLanguage)}`;
      await sendSubjectMessage(message, CONVERSATION_TYPES.TODAY_SECTION, selectedSubject);
    } catch (error) {
      console.error('Error creating today\'s section:', error);
      showAppAlert(handleApiError(error, t('errorSendingMessage', userLanguage)));
    } finally {
      setLoading(false);
    }
  };

  const loadTodaySections = async (subjectId, preselectForQuiz = false) => {
    try {
      setLoadingSections(true);
      const response = await api.get(`/learning/today-sections?subject_id=${subjectId}`);
      const sections = response.data || [];
      setTodaySections(sections);
      if (preselectForQuiz) {
        setSelectedQuizSectionIds(sections.map(section => section.id));
      }
    } catch (error) {
      console.error('Error loading today sections:', error);
      setTodaySections([]);
      if (preselectForQuiz) {
        setSelectedQuizSectionIds([]);
      }
    } finally {
      setLoadingSections(false);
    }
  };

  const handleQuizSourceChange = async (newSource) => {
    setQuizSource(newSource);
    if (newSource === 'today_sections' && selectedSubject) {
      await loadTodaySections(selectedSubject.id, true);
    }
    if (newSource !== 'today_sections') {
      setSelectedQuizSectionIds([]);
    }
  };

  const handleLearnSection = async () => {
    if (!selectedSubject || loading) return;
    
    // Load today's sections for this subject
    await loadTodaySections(selectedSubject.id);
    setShowSectionSelection(true);
  };

  const handleSelectSection = async (section) => {
    if (!selectedSubject || loading) return;
    
    closeSubjectModal();
    setShowSectionSelection(false);
    
    // Send message about learning this specific section
    const message = `${t('learnAboutMessage', userLanguage)} ${selectedSubject.name} - ${section.topic}. ${t('section', userLanguage)}: ${section.section_content}. ${t('createLearningPathMessage', userLanguage)}`;
    await sendSubjectMessage(message, CONVERSATION_TYPES.SECTION_LEARNING, selectedSubject);
  };

  const generateQuizForSubject = async ({
    subjectId,
    selectedQuizSource = 'auto',
    selectedQuestionCount = 6,
    selectedSectionIds = []
  }) => {
    const subject = schoolSubjects.find((item) => item.id === Number(subjectId));
    if (!subject) {
      throw new Error(t('selectSubject', userLanguage));
    }
    if (selectedQuizSource === 'today_sections' && selectedSectionIds.length === 0) {
      throw new Error(t('selectQuizSectionsValidation', userLanguage));
    }

    setLoading(true);
    try {
      // Call the quiz generation endpoint - use English subject name for backend
      const subjectName = subject.nameEn || subject.name; // Use English name for backend
      const queryParams = new URLSearchParams({
        subject: subjectName,
        focus_weak_topics: 'true',
        quiz_source: selectedQuizSource,
        question_count: String(selectedQuestionCount),
      });
      if (selectedQuizSource === 'today_sections') {
        selectedSectionIds.forEach((sectionId) => {
          queryParams.append('section_ids', String(sectionId));
        });
      }
      const response = await api.post(`/learning/quiz/generate?${queryParams.toString()}`, {});
      const quiz = response.data;
      setActiveQuiz(quiz);
      setQuizAnswers({});
      setQuizResult(null);
      setSelectedSubject(subject);
      setActiveTab('quiz');
      loadActiveQuizzes();
      return quiz;
    } catch (error) {
      console.error('Error generating quiz:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      
      // Check if error is about no topics
      if (errorMessage && errorMessage.includes('No topics found')) {
        showAppAlert(t('noTopicsForQuiz', userLanguage));
      } else {
        showAppAlert(handleApiError(error, String(errorMessage || t('errorSendingMessage', userLanguage))));
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuiz = async () => {
    if (!selectedSubject || loading) return;
    const selectedQuizSource = quizSource;
    const selectedQuestionCount = quizQuestionCount;
    const selectedSectionIds = selectedQuizSectionIds;
    setQuizReturnPanel(null);
    setQuizOverlayOpen(true);
    closeSubjectModal();
    try {
      await generateQuizForSubject({
        subjectId: selectedSubject.id,
        selectedQuizSource,
        selectedQuestionCount,
        selectedSectionIds
      });
    } catch (error) {
      // user-facing error already handled in generateQuizForSubject
      setQuizOverlayOpen(false);
    }
  };

  const handleGenerateQuizFromQuizTab = async ({
    subjectId,
    selectedQuizSource = 'auto',
    selectedQuestionCount = 6,
    selectedSectionIds = []
  }) => {
    setQuizReturnPanel('setup');
    setQuizOverlayOpen(true);
    try {
      return await generateQuizForSubject({
        subjectId,
        selectedQuizSource,
        selectedQuestionCount,
        selectedSectionIds
      });
    } catch (error) {
      // user-facing error already handled in generateQuizForSubject
      setQuizOverlayOpen(false);
      throw error;
    }
  };

  const fetchTodaySectionsForQuiz = async (subjectId) => {
    try {
      const response = await api.get(`/learning/today-sections?subject_id=${subjectId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error loading today sections for quiz:', error);
      return [];
    }
  };

  const handleAddSectionForSubjectFromQuiz = (subjectId) => {
    const subject = schoolSubjects.find((item) => item.id === Number(subjectId));
    if (!subject) {
      setActiveTab('subjects');
      return;
    }
    setSelectedSubject(subject);
    setShowSectionSelection(false);
    setShowTodaySectionForm(true);
    setTodayTopic('');
    setTodaySection('');
    setSubjectModalOpen(true);
  };

  const handleOpenQuizHistoryAttempt = async (attemptId) => {
    if (!attemptId || loadingQuizReview) return null;
    try {
      setLoadingQuizReview(true);
      const response = await api.get(`/learning/quiz/history/${attemptId}`);
      return response.data;
    } catch (error) {
      console.error('Error loading quiz review:', error);
      showAppAlert(handleApiError(error, t('errorSendingMessage', userLanguage)));
      return null;
    } finally {
      setLoadingQuizReview(false);
    }
  };

  const handleQuizAnswerChange = (questionId, value) => {
    setQuizAnswers((prev) => ({
      ...prev,
      [String(questionId)]: value
    }));
  };

  const handleQuizSubmit = async () => {
    if (!activeQuiz || submittingQuiz) return;
    const unanswered = (activeQuiz.questions || []).filter((question) => {
      const answer = quizAnswers[String(question.id)];
      if (typeof answer === 'number') return false;
      if (Array.isArray(answer) && answer.length > 0) return false;
      if (typeof answer === 'string' && answer.trim()) return false;
      return true;
    });

    if (unanswered.length > 0) {
      showAppAlert(t('quizCompleteAllQuestions', userLanguage));
      return;
    }

    const submitAnswers = {};
    (activeQuiz.questions || []).forEach((question) => {
      const answer = quizAnswers[String(question.id)];
      if (typeof answer === 'number') {
        submitAnswers[String(question.id)] = answer;
      } else if (Array.isArray(answer)) {
        submitAnswers[String(question.id)] = answer;
      } else if (typeof answer === 'string') {
        submitAnswers[String(question.id)] = answer;
      }
    });

    setSubmittingQuiz(true);
    try {
      const response = await api.post('/learning/quiz/attempt', {
        quiz_id: activeQuiz.id,
        answers: submitAnswers
      });
      setQuizResult(response.data);
      loadConversations();
      loadQuizHistory();
      loadActiveQuizzes();
    } catch (error) {
      console.error('Error submitting quiz:', error);
      showAppAlert(handleApiError(error, t('errorSendingMessage', userLanguage)));
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleOpenActiveQuiz = async (quizId) => {
    if (!quizId || openingActiveQuiz) return null;
    try {
      setOpeningActiveQuiz(true);
      setQuizReturnPanel('active');
      setQuizOverlayOpen(true);
      const response = await api.get(`/learning/quiz/${quizId}`);
      const loadedQuiz = response.data;
      setActiveQuiz(loadedQuiz);
      setQuizAnswers({});
      setQuizResult(null);
      setActiveTab('quiz');
      return loadedQuiz;
    } catch (error) {
      console.error('Error opening active quiz:', error);
      showAppAlert(handleApiError(error, t('errorSendingMessage', userLanguage)));
      setQuizOverlayOpen(false);
      return null;
    } finally {
      setOpeningActiveQuiz(false);
    }
  };

  const closeActiveQuizOverlay = () => {
    setQuizOverlayOpen(false);
    setActiveQuiz(null);
    setQuizAnswers({});
    setQuizResult(null);
    setActiveTab('quiz');
  };

  const handleQuizReturnPanelApplied = () => {
    setQuizReturnPanel(null);
  };

  const findSectionLearningConversation = (subjectContext = null) => {
    const sectionConversations = conversations
      .filter(conv => conv.conversation_type === CONVERSATION_TYPES.SECTION_LEARNING)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    if (sectionConversations.length === 0) {
      return null;
    }

    if (!subjectContext) {
      return sectionConversations[0];
    }

    const normalizedSubjectKey = subjectContext?.key
      ? String(subjectContext.key).toLowerCase()
      : null;

    if (normalizedSubjectKey) {
      const strictMatch = sectionConversations.find(
        conversation => (conversation.subject_key || '').toLowerCase() === normalizedSubjectKey
      );
      if (strictMatch) {
        return strictMatch;
      }
    }

    const subjectSignals = [subjectContext.name, subjectContext.nameEn, subjectContext.key]
      .filter(Boolean)
      .map(value => String(value).toLowerCase());

    if (subjectSignals.length === 0) {
      return sectionConversations[0];
    }

    const matchedConversation = sectionConversations.find((conversation) => {
      const searchableText = `${conversation.title || ''} ${conversation.last_message_preview || ''}`.toLowerCase();
      return subjectSignals.some(signal => searchableText.includes(signal));
    });

    return matchedConversation || sectionConversations[0];
  };

  const sendSubjectMessage = async (message, conversationType = null, subjectContext = null) => {
    if (!message.trim() || loading) return;

    // Switch to chat tab to see the conversation
    setActiveTab('chat');
    
    let targetConversation = currentConversation;
    let shouldStartNew = conversationType && conversationType !== CONVERSATION_TYPES.GENERAL;

    // Section learning should continue the previous relevant section conversation when possible.
    if (conversationType === CONVERSATION_TYPES.SECTION_LEARNING) {
      const existingSectionConversation = findSectionLearningConversation(subjectContext);
      if (existingSectionConversation) {
        targetConversation = existingSectionConversation;
        shouldStartNew = false;
        if (currentConversation?.id !== existingSectionConversation.id) {
          setCurrentConversation(existingSectionConversation);
        }
      } else {
        targetConversation = null;
        shouldStartNew = true;
      }
    }
    
    // Reset conversation and messages if starting new
    if (shouldStartNew) {
      setCurrentConversation(null);
      setMessages([]);
    }
    
    setInputMessage('');
    setLoading(true);

    // Add user message to UI immediately
    const newUserMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const requestData = {
        message: message,
        conversation_id: shouldStartNew ? null : targetConversation?.id,
        conversation_type: conversationType || CONVERSATION_TYPES.GENERAL,  // Always send conversation_type
        subject_key: subjectContext?.key ? String(subjectContext.key).toLowerCase() : undefined,
      };
      
      const response = await api.post('/chat', requestData);

      const { response: aiResponse, conversation_id } = response.data;

      // Add AI response to UI
      const newAiMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, newAiMessage]);

      // Update current conversation if it's a new one
      if (!currentConversation) {
        // Reload conversations to get the full conversation data including type
        const updatedConversations = await api.get('/conversations');
        const newConv = updatedConversations.data.find(c => c.id === conversation_id);
        if (newConv) {
          setCurrentConversation(newConv);
          setConversations(updatedConversations.data);
        } else {
          // Fallback: create basic conversation object
          const newConversation = {
            id: conversation_id,
            title: message.length > 50 ? message.substring(0, 50) + '...' : message,
            created_at: new Date().toISOString(),
            conversation_type: conversationType || CONVERSATION_TYPES.GENERAL,
            subject_key: subjectContext?.key ? String(subjectContext.key).toLowerCase() : null,
          };
          setCurrentConversation(newConversation);
          setConversations(prev => [newConversation, ...prev]);
        }
      } else {
        // Reload conversations to get updated titles and types
        loadConversations();
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
      const errorMessage = handleApiError(error, t('errorSendingMessage', userLanguage));
      showAppAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get school subjects from assigned classes - useMemo to recalculate when language or classes change
  const schoolSubjects = useMemo(() => {
    if (loadingClasses || assignedClasses.length === 0) {
      return [];
    }
    
    // Map assigned classes to the format expected by SubjectsTab
    return assignedClasses.map(classItem => ({
      id: classItem.id,
      key: classItem.key,
      name: classItem.name || classItem.key,
      nameEn: classItem.key, // Use key for backend (e.g., "math", "science") instead of translated name
      emoji: classItem.emoji || '📚',
      description: classItem.description || ''
    }));
  }, [assignedClasses, loadingClasses]);

  return (
    <div className="dashboard-container kids-dashboard-view">
      <div className="rulee-navbar">
        <div className="rulee-navbar-content">
          <h1>RuleeAI</h1>
          <div className="rulee-navbar-tabs">
            <button
              className={`rulee-navbar-tab ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={handleChatTabClick}
            >
              {t('navChat', userLanguage)}
            </button>
            <button
              className={`rulee-navbar-tab ${activeTab === 'quiz' ? 'active' : ''}`}
              onClick={() => setActiveTab('quiz')}
            >
              {t('navQuiz', userLanguage)}
            </button>
            <button
              className={`rulee-navbar-tab ${activeTab === 'conversations' ? 'active' : ''}`}
              onClick={() => setActiveTab('conversations')}
            >
              {t('navConversations', userLanguage)}
            </button>
            <button
              className={`rulee-navbar-tab ${activeTab === 'subjects' ? 'active' : ''}`}
              onClick={() => setActiveTab('subjects')}
            >
              {t('navSubjects', userLanguage)}
            </button>
          </div>
          <div className="rulee-navbar-actions">
            {isParent && (
              <button
                className="btn-secondary"
                onClick={() => navigate('/parent')}
              >
                {t('navParentDashboard', userLanguage)}
              </button>
            )}
            <ThemeToggle language={userLanguage} />
            <button className="btn-logout" onClick={logout}>
              {t('logout', userLanguage)}
            </button>
          </div>
          <button
            className="rulee-navbar-hamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label={t('openMenu', userLanguage)}
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      <div
        className={`rulee-mobile-menu-backdrop ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`rulee-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="rulee-mobile-menu-header">
          <h2>{t('menu', userLanguage)}</h2>
          <button
            className="rulee-mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label={t('closeMenu', userLanguage)}
          >
            ×
          </button>
        </div>
        <div className="rulee-mobile-menu-tabs">
          <button
            className={`rulee-mobile-menu-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => {
              handleChatTabClick();
              setMobileMenuOpen(false);
            }}
          >
            {t('navChat', userLanguage)}
          </button>
          <button
            className={`rulee-mobile-menu-tab ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('conversations');
              setMobileMenuOpen(false);
            }}
          >
            {t('navConversations', userLanguage)}
          </button>
          <button
            className={`rulee-mobile-menu-tab ${activeTab === 'quiz' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('quiz');
              setMobileMenuOpen(false);
            }}
          >
            {t('navQuiz', userLanguage)}
          </button>
          <button
            className={`rulee-mobile-menu-tab ${activeTab === 'subjects' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('subjects');
              setMobileMenuOpen(false);
            }}
          >
            {t('navSubjects', userLanguage)}
          </button>
        </div>
        <div className="rulee-mobile-menu-actions">
          {isParent && (
            <button
              className="btn-secondary"
              onClick={() => {
                navigate('/parent');
                setMobileMenuOpen(false);
              }}
            >
              {t('navParentDashboard', userLanguage)}
            </button>
          )}
          <ThemeToggle language={userLanguage} />
          <button
            className="btn-logout"
            onClick={() => {
              logout();
              setMobileMenuOpen(false);
            }}
          >
            {t('logout', userLanguage)}
          </button>
        </div>
      </div>

      {/* Chat Tab */}
      {activeTab === 'chat' && (
        <ChatTab
          currentConversation={currentConversation}
          messages={messages}
          inputMessage={inputMessage}
          loading={loading}
          loadingUserInfo={loadingUserInfo}
          loadingConversations={loadingConversations}
          userName={userName}
          userLanguage={userLanguage}
          onSendMessage={sendMessage}
          setInputMessage={setInputMessage}
        />
      )}

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <ConversationsTab
          conversations={conversations}
          loadingConversations={loadingConversations}
          currentConversation={currentConversation}
          onSelectConversation={selectConversation}
          onStartNewConversation={startNewConversation}
          setActiveTab={setActiveTab}
          userLanguage={userLanguage}
        />
      )}

      {/* Quiz Tab (setup/history and in-page active quiz) */}
      {activeTab === 'quiz' && !quizOverlayOpen && (
        <QuizTab
          quiz={activeQuiz}
          subjects={schoolSubjects}
          completedQuizzes={completedQuizzes}
          loadingQuizHistory={loadingQuizHistory}
          activeQuizzes={activeQuizzes}
          loadingActiveQuizzes={loadingActiveQuizzes}
          openingActiveQuiz={openingActiveQuiz}
          answers={quizAnswers}
          loading={submittingQuiz}
          generatingQuiz={loading}
          loadingQuizReview={loadingQuizReview}
          result={quizResult}
          userLanguage={userLanguage}
          onBack={() => setActiveTab('subjects')}
          onCancelQuiz={closeActiveQuizOverlay}
          onClose={closeActiveQuizOverlay}
          fullscreen={false}
          onSubmit={handleQuizSubmit}
          onGenerateNewQuiz={handleGenerateQuizFromQuizTab}
          onAddSectionForSubject={handleAddSectionForSubjectFromQuiz}
          onOpenQuizHistoryAttempt={handleOpenQuizHistoryAttempt}
          onOpenActiveQuiz={handleOpenActiveQuiz}
          onLoadSections={fetchTodaySectionsForQuiz}
          onAnswerChange={handleQuizAnswerChange}
          initialOpenPanel={quizReturnPanel}
          onInitialOpenPanelApplied={handleQuizReturnPanelApplied}
        />
      )}

      {/* Fullscreen active quiz overlay */}
      {quizOverlayOpen && (
        <div className="quiz-fullscreen-overlay">
          <div className="quiz-fullscreen-content">
            <QuizTab
              quiz={activeQuiz}
              subjects={schoolSubjects}
              completedQuizzes={completedQuizzes}
              loadingQuizHistory={loadingQuizHistory}
              activeQuizzes={activeQuizzes}
              loadingActiveQuizzes={loadingActiveQuizzes}
              openingActiveQuiz={openingActiveQuiz}
              answers={quizAnswers}
              loading={submittingQuiz}
              generatingQuiz={loading}
              loadingQuizReview={loadingQuizReview}
              result={quizResult}
              userLanguage={userLanguage}
              onBack={() => setActiveTab('subjects')}
            onCancelQuiz={closeActiveQuizOverlay}
              onClose={closeActiveQuizOverlay}
              fullscreen
              onSubmit={handleQuizSubmit}
              onGenerateNewQuiz={handleGenerateQuizFromQuizTab}
              onAddSectionForSubject={handleAddSectionForSubjectFromQuiz}
              onOpenQuizHistoryAttempt={handleOpenQuizHistoryAttempt}
              onOpenActiveQuiz={handleOpenActiveQuiz}
              onLoadSections={fetchTodaySectionsForQuiz}
              onAnswerChange={handleQuizAnswerChange}
              initialOpenPanel={null}
              onInitialOpenPanelApplied={undefined}
            />
          </div>
        </div>
      )}

      {/* Subjects Tab */}
      {activeTab === 'subjects' && (
        <SubjectsTab
          schoolSubjects={schoolSubjects}
          loading={loading || loadingClasses}
          onSubjectClick={handleSubjectClick}
          userLanguage={userLanguage}
        />
      )}

      {/* Subject Modal */}
      {subjectModalOpen && (
        <SubjectModal
          selectedSubject={selectedSubject}
          showTodaySectionForm={showTodaySectionForm}
          showSectionSelection={showSectionSelection}
          todayTopic={todayTopic}
          todaySection={todaySection}
          todaySections={todaySections}
          loading={loading}
          loadingSections={loadingSections}
          userLanguage={userLanguage}
          quizSource={quizSource}
          quizQuestionCount={quizQuestionCount}
          selectedQuizSectionIds={selectedQuizSectionIds}
          onClose={closeSubjectModal}
          onAddTodaysSection={handleAddTodaysSection}
          onSubmitTodaysSection={handleSubmitTodaysSection}
          onLearnSection={handleLearnSection}
          onSelectSection={handleSelectSection}
          onGenerateQuiz={handleGenerateQuiz}
          onQuizSourceChange={handleQuizSourceChange}
          setQuizQuestionCount={setQuizQuestionCount}
          setSelectedQuizSectionIds={setSelectedQuizSectionIds}
          setTodayTopic={setTodayTopic}
          setTodaySection={setTodaySection}
          setShowTodaySectionForm={setShowTodaySectionForm}
          setShowSectionSelection={setShowSectionSelection}
        />
      )}

      {alertMessage && (
        <div className="modal-overlay app-alert-overlay" onClick={() => setAlertMessage('')}>
          <div className="modal-content app-alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('noticeTitle', userLanguage)}</h3>
              <button
                className="modal-close-btn"
                onClick={() => setAlertMessage('')}
                aria-label={t('closeAlert', userLanguage)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{alertMessage}</p>
              <div className="form-actions">
                <button type="button" className="btn" onClick={() => setAlertMessage('')}>
                  {t('okLabel', userLanguage)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rulee;

